import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtRM, fmtDate } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Plane, Receipt, FileCheck, UserRound, CreditCard } from "lucide-react";

const BK_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  deposit_paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  confirmed: "bg-green-100 text-green-800 border-green-300",
  travelled: "bg-sky-50 text-sky-700 border-sky-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const VISA_STYLE = {
  not_submitted: "bg-slate-100 text-slate-600 border-slate-300",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  processing: "bg-indigo-50 text-indigo-700 border-indigo-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  issued: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function CustomerPortal() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const customerId = params.get("customer_id");

  const payOnline = async (b) => {
    setPaying(b.booking_id);
    try {
      const res = await api.post("/payments/checkout", { booking_id: b.booking_id, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setPaying(null);
    }
  };

  useEffect(() => {
    const qs = customerId ? `?customer_id=${customerId}` : "";
    api.get(`/portal/summary${qs}`).then((r) => setData(r.data)).catch(() => setError("Portal data unavailable"));
  }, [customerId]);

  useEffect(() => {
    if (data?.viewing_as_admin && customerList.length === 0) {
      api.get("/customers").then((r) => setCustomerList(r.data)).catch(() => {});
    }
  }, [data, customerList.length]);

  if (error) return <div className="text-center py-20 text-muted-foreground" data-testid="portal-error">{error}</div>;
  if (!data) return <div className="flex justify-center h-64 items-center" data-testid="portal-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  const { customer: c, bookings, payments, totals, viewing_as_admin } = data;
  const nextTrip = bookings.find((b) => b.departure_date >= new Date().toISOString().slice(0, 10));
  const initials = (c.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("");

  const docs = [
    { label: "Passport", ok: !!c.passport, detail: c.passport ? `${c.passport} • exp ${fmtDate(c.passport_expiry)}` : "Not submitted" },
    { label: "Visa", ok: ["approved", "issued"].includes(c.visa_status), detail: (c.visa_status || "not_submitted").replace(/_/g, " ") },
    { label: "MyKad", ok: !!c.mykad, detail: c.mykad || "Not submitted" },
    { label: "Receipts", ok: payments.length > 0, detail: `${payments.length} issued` },
  ];

  return (
    <div className="space-y-5" data-testid="portal-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.portal")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("portal.sub")}</p>
        </div>
      </div>

      {viewing_as_admin && (
        <div data-testid="portal-admin-banner" className="flex flex-wrap items-center justify-between gap-3 bg-gold-100/60 border border-gold-500/30 rounded-xl px-4 py-3 text-sm text-gold-700">
          <div className="flex items-center gap-2.5">
            <Eye className="w-4 h-4 shrink-0" />
            <span>{t("portal.viewing")} <strong>{c.name}</strong></span>
          </div>
          <Select value={c.customer_id} onValueChange={(v) => setParams({ customer_id: v })}>
            <SelectTrigger data-testid="portal-customer-select" className="w-64 h-9 bg-white text-slate-800 text-xs border-gold-500/30">
              <SelectValue placeholder="Select jemaah" />
            </SelectTrigger>
            <SelectContent>
              {customerList.map((cu) => (
                <SelectItem key={cu.customer_id} value={cu.customer_id} data-testid={`portal-customer-option-${cu.customer_id}`}>{cu.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Profile + stats */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="pro-card p-6" data-testid="portal-profile">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-brand-800 text-sand flex items-center justify-center font-serif text-xl">{initials}</div>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate">{c.name}</div>
              <div className="text-xs text-muted-foreground">{c.phone}</div>
              <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase mt-1.5 ${VISA_STYLE[c.visa_status] || VISA_STYLE.not_submitted}`} data-testid="portal-visa-badge">
                Visa: {(c.visa_status || "not_submitted").replace(/_/g, " ")}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-sand-border text-xs">
            <div><div className="text-muted-foreground font-mono uppercase text-[9px] tracking-wider">MyKad</div><div className="font-mono mt-0.5">{c.mykad || "—"}</div></div>
            <div><div className="text-muted-foreground font-mono uppercase text-[9px] tracking-wider">Passport</div><div className="font-mono mt-0.5">{c.passport || "—"}</div></div>
          </div>
        </div>

        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: FileCheck, label: t("portal.bookings"), value: totals.bookings, testid: "portal-stat-bookings" },
            { icon: UserRound, label: "Pax", value: totals.pax, testid: "portal-stat-pax" },
            { icon: CreditCard, label: t("portal.total_paid"), value: fmtRM(totals.paid), testid: "portal-stat-paid", gold: true },
            { icon: Receipt, label: t("common.outstanding"), value: fmtRM(totals.outstanding), testid: "portal-stat-outstanding" },
          ].map(({ icon: Icon, label, value, testid, gold }) => (
            <div key={testid} className="pro-card p-4 flex flex-col justify-between" data-testid={testid}>
              <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${gold ? "bg-gold-100/60 border-gold-500/25" : "bg-emerald-50 border-emerald-100"}`}>
                <Icon className={`w-4 h-4 ${gold ? "text-gold-600" : "text-brand-700"}`} />
              </div>
              <div className="mt-3">
                <div className={`font-sans text-lg font-bold tabular-nums ${gold ? "text-gold-700" : "text-slate-900"}`}>{value}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Next trip */}
      {nextTrip && (
        <div className="rounded-2xl bg-brand-950 text-sand p-6 relative overflow-hidden" data-testid="portal-next-trip">
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold-500">Next Departure</div>
              <div className="font-serif text-2xl mt-1.5">{nextTrip.package_name}</div>
              <div className="text-sand/60 text-sm mt-1 flex items-center gap-2">
                <Plane className="w-4 h-4 text-gold-500" />
                {fmtDate(nextTrip.departure_date)} → {fmtDate(nextTrip.return_date)} • {nextTrip.flight || "TBA"} • {nextTrip.pax} pax • {nextTrip.room_type}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-sand/50">Mutawif</div>
              <div className="font-medium mt-1">{nextTrip.mutawif || "TBA"}</div>
            </div>
          </div>
        </div>
      )}

      {/* Bookings + documents */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 pro-card p-5" data-testid="portal-bookings">
          <h2 className="font-sans font-semibold text-slate-900 mb-4">{t("portal.bookings")}</h2>
          <div className="space-y-4">
            {bookings.map((b) => {
              const pct = b.total > 0 ? Math.round((b.paid / b.total) * 100) : 0;
              return (
                <div key={b.booking_id} className="border border-sand-border rounded-xl p-4" data-testid={`portal-booking-${b.booking_no}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{b.package_name}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">{b.booking_no} • {fmtDate(b.departure_date)} • {b.pax} pax • {b.room_type}</div>
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase ${BK_STYLE[b.status] || BK_STYLE.pending}`} data-testid={`portal-booking-status-${b.booking_no}`}>
                      {b.status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <Progress value={pct} className="h-2 flex-1" />
                    <span className="text-xs font-mono text-muted-foreground whitespace-nowrap tabular-nums">{fmtRM(b.paid)} / {fmtRM(b.total)}</span>
                  </div>
                  {b.outstanding > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                      <div className="text-xs text-gold-700 font-mono">Outstanding: {fmtRM(b.outstanding)}</div>
                      <button data-testid={`pay-online-${b.booking_no}`} onClick={() => payOnline(b)}
                        disabled={paying === b.booking_id}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-800 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-50">
                        <CreditCard className="w-3.5 h-3.5" />{paying === b.booking_id ? "Redirecting…" : "Pay Online"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {bookings.length === 0 && <div className="text-center py-10 text-muted-foreground text-sm">No bookings yet</div>}
          </div>
        </div>

        <div className="pro-card p-5" data-testid="portal-documents">
          <h2 className="font-sans font-semibold text-slate-900 mb-4">{t("portal.docs")}</h2>
          <div className="space-y-3">
            {docs.map((d) => (
              <div key={d.label} className="flex items-center gap-3" data-testid={`portal-doc-${d.label.toLowerCase()}`}>
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${d.ok ? "bg-emerald-500" : "bg-amber-400"}`} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{d.label}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Receipts */}
      <div className="pro-card overflow-x-auto" data-testid="portal-receipts">
        <h2 className="font-sans font-semibold text-slate-900 p-5 pb-3">{t("portal.receipts")}</h2>
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="border-y border-sand-border text-left bg-[#F8F6F2]/80">
              {["Receipt", "Booking", "Amount", "Method", t("common.date")].map((h) => (
                <th key={h} className="px-5 py-2.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.payment_id} className="border-b border-sand-border/60 hover:bg-emerald-50/60 transition-colors" data-testid={`portal-receipt-${p.receipt_no}`}>
                <td className="px-5 py-3 font-mono text-xs text-brand-800">{p.receipt_no}</td>
                <td className="px-5 py-3 font-mono text-xs">{p.booking_no}</td>
                <td className="px-5 py-3 font-mono text-xs font-semibold text-emerald-700">{fmtRM(p.amount)}</td>
                <td className="px-5 py-3 text-xs capitalize">{p.method.replace(/_/g, " ")}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground">{fmtDate(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {payments.length === 0 && <div className="text-center py-10 text-muted-foreground text-sm">No receipts yet</div>}
      </div>
    </div>
  );
}
